import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid, makeStyles } from "@material-ui/core";
import dayjs from "dayjs";
import * as React from "react";
import { useForm, Controller } from "react-hook-form";

import { MonthlyCashflowResult } from "@/api/generated";
import { CustomButton } from "@/components/Button";
import { PropertyEditErrorSummary } from "@/components/property_edit/PropertyEditErrorSummary";
import { CustomForm } from "@/components/property_edit/PropertyEditInputs";
import theme from "@/theme";
import { parseFormattedNumber } from "@/utilities/format";
import { createInsuranceInfoHistorySchema, InsuranceInfoHistoryFormData } from "@/validations/management/edit";
import { PropertyHolder } from "@/view_models/atoms";

import { useEditMonthlyCashflowResult } from "../../../hooks/useEditMonthlyCashflowResult";

import { getInsuranceTypeKeys, InsuranceType } from "./types";

const useStyles = makeStyles(() => ({
  background: {
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    padding: 16,
  },
}));

interface props {
  monthlyCashflowResult?: MonthlyCashflowResult;
  insuranceType: InsuranceType;
  onCancel: () => void;
  onSave?: () => void;
  onCreate?: () => void;
  propertyHolder: PropertyHolder;
}

// 履歴編集用コンポーネント
export const InsuranceInfoHistoryEditor: React.FC<props> = ({
  monthlyCashflowResult,
  insuranceType,
  onCancel,
  onSave,
  onCreate,
  propertyHolder,
}) => {
  const classes = useStyles();

  // 動的なスキーマを作成（購入月チェック付き）
  const dynamicSchema = createInsuranceInfoHistorySchema(propertyHolder.property.bought_at);

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<InsuranceInfoHistoryFormData>({
    resolver: zodResolver(dynamicSchema),
    defaultValues: {
      month: dayjs(monthlyCashflowResult?.month).format("YYYY-MM") || "",
      fire_insurance_cost: monthlyCashflowResult?.fire_insurance_cost?.toString() || "",
      fire_insurance_holding_period: monthlyCashflowResult?.fire_insurance_holding_period?.toString() || "",
      earthquake_insurance_cost: monthlyCashflowResult?.earthquake_insurance_cost?.toString() || "",
      earthquake_insurance_holding_period: monthlyCashflowResult?.earthquake_insurance_holding_period?.toString() || "",
    },
  });

  const { editMonthlyCashflowResult } = useEditMonthlyCashflowResult();

  const onSubmit = async (data: InsuranceInfoHistoryFormData) => {
    const originalMonth = monthlyCashflowResult?.month;
    const newMonth = `${data.month}-01`;

    const fieldsToDelete = getInsuranceTypeKeys(insuranceType);

    const sendData = {
      ...data,
      month: newMonth,
      ...(data.fire_insurance_cost && {
        fire_insurance_cost: parseFormattedNumber(data.fire_insurance_cost),
      }),
      ...(data.earthquake_insurance_cost && {
        earthquake_insurance_cost: parseFormattedNumber(data.earthquake_insurance_cost),
      }),
    };

    await editMonthlyCashflowResult(fieldsToDelete, sendData, originalMonth);

    if (onSave) {
      onSave();
    } else if (onCreate) {
      onCreate();
    }
  };

  return (
    <Box className={classes.background} data-edit-form-panel>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box mb={2}>
          <Controller
            name="month"
            control={control}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="開始月"
                required
                type="month"
                error={!!errors.month}
                errorText={errors.month?.message}
              />
            )}
          />
          {insuranceType === "火災保険" && (
            <>
              <Controller
                name="fire_insurance_cost"
                control={control}
                render={({ ...field }) => (
                  <CustomForm
                    {...field}
                    label="火災保険料"
                    required
                    error={!!errors.fire_insurance_cost}
                    errorText={errors.fire_insurance_cost?.message}
                    placeHolder="100,000,000"
                    unitSuffix="円"
                    formatNumeric
                  />
                )}
              />

              <Controller
                name="fire_insurance_holding_period"
                control={control}
                render={({ ...field }) => (
                  <CustomForm
                    {...field}
                    label="火災保険期間(年数)"
                    required
                    error={!!errors.fire_insurance_holding_period}
                    errorText={errors.fire_insurance_holding_period?.message}
                    placeHolder="10"
                    unitSuffix="年"
                  />
                )}
              />
            </>
          )}
          {insuranceType === "地震保険" && (
            <>
              <Controller
                name="earthquake_insurance_cost"
                control={control}
                render={({ ...field }) => (
                  <CustomForm
                    {...field}
                    label="地震保険料"
                    required
                    error={!!errors.earthquake_insurance_cost}
                    errorText={errors.earthquake_insurance_cost?.message}
                    placeHolder="100,000,000"
                    unitSuffix="円"
                    formatNumeric
                  />
                )}
              />
              <Controller
                name="earthquake_insurance_holding_period"
                control={control}
                render={({ ...field }) => (
                  <CustomForm
                    {...field}
                    label="地震保険期間(年数)"
                    required
                    error={!!errors.earthquake_insurance_holding_period}
                    errorText={errors.earthquake_insurance_holding_period?.message}
                    placeHolder="10"
                    unitSuffix="年"
                  />
                )}
              />
            </>
          )}
        </Box>

        <PropertyEditErrorSummary errors={errors} />
        <Grid container spacing={2} data-edit-form-actions>
          <Grid item xs={6}>
            <CustomButton customVariant="textPrimary" onClick={onCancel} size="small" fullWidth>
              キャンセル
            </CustomButton>
          </Grid>
          <Grid item xs={6}>
            <CustomButton buttonSize="L" customVariant="filled" type="submit" size="small" fullWidth>
              保存
            </CustomButton>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};
